import { IframeWidget } from '.';
import { Log } from '../../AuxillaryService/Log';
import { asyncTimeout } from '../../Utility/AsyncTimeout';
import { BaseIframeWidget } from './types';

/**
 * Windowed Iframe
 * @class
 * @augments VirtualIframe
 * @param {Widget} widget
 */
export class WindowedIframe implements BaseIframeWidget {
    /**
     * the handle to the iframe UI window
     */
    handle = -1;

    /**
     * Iframe widget object
     */
    widget: IframeWidget;

    constructor(widget: IframeWidget) {
        this.widget = widget;
    }

    /**
     * Draws the iframe window onto the UI
     */
    render(): void {
        if (!REACH.Application) {
            Log.Warning('REACH.Application not initialized.', null, __fileline, null);
            return;
        }
        const bounds = this.widget.wrapperElement.getBoundingClientRect();
        this.handle = REACH.Application.CreateChromeWindow(
            this.widget.config.url,
            Math.round(bounds.left),
            Math.round(bounds.top),
            Math.round(bounds.width),
            Math.round(bounds.height)
        );
    }

    /**
     * removes the iframe window from the UI
     */
    async destroy(): Promise<void> {
        await REACH.Application?.DestroyChromeWindow(this.handle);
        this.handle = -1;
    }

    /**
     * executes the given JS on the windowed Iframe
     * @returns JSON string on success or an empty string on failure
     */
    executeJS(
        jsToExec: string,
        executeInline?: boolean,
        wrapEval?: boolean
    ): Promise<string | undefined> | string | undefined {
        if (executeInline) {
            if (wrapEval === false) {
                return REACH.Application?.ExecuteJSOnChromeWindow(this.handle, jsToExec, false);
            } else {
                return REACH.Application?.ExecuteJSOnChromeWindow(this.handle, jsToExec);
            }
        }

        if (wrapEval === false) {
            return Promise.resolve(
                REACH.Application?.ExecuteJSOnChromeWindow(this.handle, jsToExec, false)
            );
        } else {
            return Promise.resolve(
                REACH.Application?.ExecuteJSOnChromeWindow(this.handle, jsToExec)
            );
        }
    }

    /**
     * Attempt to load iframe. UI display handled by virtual iframe
     */
    async attemptIframeLoad(): Promise<void> {
        if (!this.widget.loginScriptStarted) {
            this.widget.loginScriptStarted = true;
            // ignored promise.
            this.widget.startLoginScript();
        }

        /**
         * Tell the browser to refresh.
         * ignored promise.
         */
        this.refreshWindowedIframe();
    }

    /**
     * Creates a setTimeout to wait for the iframe reload to run, then refreshes
     *   the styling after the new iframe body and scripts are loaded.
     */
    async refreshWindowedIframe(): Promise<void> {
        this.executeJS('location.reload()');

        // wait for the reload to run
        await asyncTimeout(1000);
        // worker to watch for when the iframe has loaded
        const waitUntilLoaded = setInterval(async () => {
            const bodyLoaded =
                (await this.executeJS('document.querySelector("body") !== null', true)) === 'true';

            if (bodyLoaded) {
                clearInterval(waitUntilLoaded);

                // wait until any JS on the page has run
                await asyncTimeout(1000);
                // apply the CSS for scroll, scaling, and custom styling/CSS
                let additionalStyling = ';';
                const css = this.widget.getIframeCSS();

                Object.entries(css).forEach(([key, value]) => {
                    if (key !== 'width' && key !== 'height') {
                        additionalStyling += `${key}:${value};`;
                    }
                });

                this.executeJS(
                    'document.body.insertBefore(document.createElement("style"),' +
                        ' document.body.firstChild).innerHTML = "html { ' +
                        (this.widget.config.css ?? '') +
                        additionalStyling +
                        ' }"'
                );
            }
        }, 100);
    }
}
